import { CloseRounded } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import ReactGA from 'react-ga4';
import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';

import { SharedDestinationEntryVM, SharedDestinationVM } from './sharedDestinationVM';

// buildExpandedRowVM.ts:9-10（元は LaneCard.tsx:15-16）と同じ書式。
// 同じ単位を画面内で同じ見た目にするため、区切りと単位もそちらに合わせる

const casesText = (value: number) => `${quantityValueText(value)}cs`;
const weightText = (value: number) => `${weightValueText(value)}kg`;
const volumeText = (value: number) => `${volumeValueText(value)}m³`;
const invoiceText = (value: number) => `${value}枚`;

/** 色が引けない行でも枠を残す（行の並びを崩さない） */
const FALLBACK_COLOR = 'hsl(var(--muted-foreground) / 0.4)';

export interface SharedDestinationPanelProps {
  vm: SharedDestinationVM;
  /** GA の screen_name。器（DetailPanelShell）と同じく呼び出し側から受け取る */
  screenName: string;
  /** 「この配車の詳細」。押された行の entry をそのまま返す（カードの解決は呼び出し側） */
  onSelectEntry: (entry: SharedDestinationEntryVM) => void;
  onClose: () => void;
}

/**
 * 見出しと値の 2 段組み。数字は等幅で並べて桁を目で揃える
 * （サンプル MapPage.tsx:2257-2268 と同じ）。
 */
function MetricCell({ label, value, align }: { label: string; value: string; align: 'center' | 'flex-start' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: align, minWidth: 0 }}>
      <Box sx={{ fontSize: 9, color: 'hsl(var(--muted-foreground))', lineHeight: 1.4 }}>{label}</Box>
      <Box
        sx={{
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'monospace',
          color: 'hsl(var(--foreground))',
          lineHeight: 1.4,
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

/**
 * 共通配送先ポップアップ。同じ地点を複数車両で分納しているときに、詳細ではなく車両一覧を出す。
 *
 * **描画のみ**。判定（placeKey の一致・distinct 車両が 2 台以上）と VM の構築は `/map` の
 * Presenter が行う（buildSharedDestinationVM）。seriesIndex と color は地図マーカー・
 * 走行ルート線と同じ値を受け取り、ここでは再計算しない。
 *
 * 位置は詳細パネル（DetailPanelShell の placement='mapTopRight'）と同じ地図右上・同じ幅で、
 * 排他表示にする前提。両方開くと重なるため、開閉の制御は呼び出し側が持つ。
 */
export function SharedDestinationPanel({ vm, screenName, onSelectEntry, onClose }: SharedDestinationPanelProps) {
  return (
    <Box
      data-testid="shared-destination-panel"
      sx={{
        position: 'absolute',
        // 地図右上の表示フィルタ (Map/MapDisplayFilter.tsx) の下。DetailPanelShell の
        // mapTopRight と同じ値にする。重ねるとどちらも操作できない
        top: 44,
        right: 16,
        zIndex: 1100,
        width: 380,
        maxHeight: 'calc(100% - 60px)',
        overflowY: 'auto',
        borderRadius: '12px',
        border: '1px solid hsl(var(--border))',
        bgcolor: 'hsl(var(--background))',
        boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      }}
    >
      {/* 見出し（地点・合計）はスクロールしても残す。どの地点の話か見失わないため */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          px: 2,
          pt: 1.5,
          pb: 1.25,
          bgcolor: 'hsl(var(--background))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: '2px' }}>
              <Box
                component="span"
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  px: 0.5,
                  py: '1px',
                  borderRadius: '4px',
                  color: 'hsl(210 80% 50%)',
                  border: '1px solid hsl(210 80% 50% / 0.35)',
                  bgcolor: 'hsl(210 80% 50% / 0.14)',
                }}
              >
                降地
              </Box>
              {/* 分納は「気づいてほしい」情報なので塗りつぶす */}
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 0.75,
                  py: '1px',
                  borderRadius: '10px',
                  color: '#fff',
                  bgcolor: 'hsl(30 90% 45%)',
                }}
              >
                {`${vm.vehicleCount}台で分納`}
              </Box>
            </Box>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: 'hsl(var(--foreground))', lineHeight: 1.25 }}>
              {vm.storeName}
            </Box>
            {vm.address && (
              <Box sx={{ mt: '2px', fontSize: 11, color: 'hsl(var(--muted-foreground))', lineHeight: 1.3 }}>
                {vm.address}
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="閉じる" sx={{ width: 30, height: 30 }}>
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box
          data-testid="shared-destination-totals"
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.5,
            p: 1,
            borderRadius: '8px',
            bgcolor: 'hsl(var(--secondary) / 0.4)',
          }}
        >
          <MetricCell label="合計数量" value={casesText(vm.totals.cases)} align="center" />
          <MetricCell label="合計重量" value={weightText(vm.totals.weight)} align="center" />
          <MetricCell label="合計体積" value={volumeText(vm.totals.volume)} align="center" />
          <MetricCell label="伝票数" value={invoiceText(vm.totals.invoiceCount)} align="center" />
        </Box>
      </Box>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {vm.entries.map((entry) => (
          // 1 訪問 1 枚。同じ車両が同じ地点を再訪すると rowId は同じで seriesIndex が変わる
          <Box
            key={`${entry.rowId}-${entry.seriesIndex}`}
            data-testid="shared-destination-entry"
            sx={{
              borderRadius: '10px',
              // shorthand で書くと jsdom が展開せずテストで判定できない
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'hsl(var(--border))',
              bgcolor: 'hsl(var(--background))',
              // 色帯をカードの角に合わせて切る
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Box sx={{ width: 4, flexShrink: 0, bgcolor: entry.color || FALLBACK_COLOR }} />
              <Box sx={{ flex: 1, minWidth: 0, p: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    {/* 地図のマーカーと同じ番号・同じ色。丸にすることでマーカーと結びつく */}
                    <Box
                      data-testid={`shared-destination-index-${entry.seriesIndex}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        flexShrink: 0,
                        borderRadius: '9999px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#fff',
                        bgcolor: entry.color || FALLBACK_COLOR,
                      }}
                    >
                      {entry.seriesIndex}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'hsl(var(--foreground))',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.vehicleNumber}
                      </Box>
                      <Box
                        sx={{
                          fontSize: 10,
                          color: 'hsl(var(--muted-foreground))',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {`${entry.driverName} ・ ${entry.companyName} ・ ${entry.rotationNumber} 回転目`}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: 'hsl(var(--muted-foreground))',
                    }}
                  >
                    {`${entry.arrivalTime}着`}
                  </Box>
                </Box>

                <Box
                  data-testid="shared-destination-metrics"
                  sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.25 }}
                >
                  <MetricCell label="数量" value={casesText(entry.metrics.cases)} align="flex-start" />
                  <MetricCell label="重量" value={weightText(entry.metrics.weight)} align="flex-start" />
                  <MetricCell label="体積" value={volumeText(entry.metrics.volume)} align="flex-start" />
                  <MetricCell label="伝票" value={invoiceText(entry.metrics.invoiceCount)} align="flex-start" />
                </Box>

                <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      // 器（DetailPanelShell）の 未割当に戻す / 削除 と同じ形。
                      // ✕ は器と揃えて計測しない
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '共通配送先 この配車の詳細',
                      });
                      onSelectEntry(entry);
                    }}
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid hsl(var(--primary) / 0.5)',
                      bgcolor: 'transparent',
                      px: 1,
                      py: '3px',
                      fontSize: 11,
                      color: 'hsl(var(--primary))',
                      cursor: 'pointer',
                      transition: 'background-color 150ms',
                      '&:hover': { bgcolor: 'hsl(var(--primary) / 0.08)' },
                    }}
                  >
                    この配車の詳細
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
